import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { usePaymentConfirmation } from '../../hooks/usePaymentConfirmation';
import repositoryContainer from '../../services/RepositoryContainer';
import ClientHeaderNavigation from '../../components/ui/ClientHeaderNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const OrderPaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { companyPath } = useParams();
  const reference = searchParams.get('reference') || searchParams.get('trxref');
  const [pendingOrder] = useState(() => {
    try {
      const stored = localStorage.getItem(`clawfe:pendingOrder:${companyPath || 'global'}`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const sourceReferenceCode = pendingOrder?.referenceCode || reference;

  // 'verifying' | 'waiting' | 'confirmed' | 'failed'
  const [stage, setStage] = useState('verifying');
  const [copiedReference, setCopiedReference] = useState(false);
  const verifyAttempted = useRef(false);

  const { status: signalRStatus, timeoutReached } = usePaymentConfirmation(
    stage === 'waiting' ? reference : null,
    async () => {
      if (sourceReferenceCode) {
        await repositoryContainer.order.getByReferenceCode(sourceReferenceCode).catch(() => null);
      }
      setStage('confirmed');
    }
  );

  useEffect(() => {
    if (!reference || verifyAttempted.current) return;
    verifyAttempted.current = true;

    const attemptVerify = async () => {
      try {
        await repositoryContainer.order.verifyPayment(reference);
        if (sourceReferenceCode) {
          await repositoryContainer.order.getByReferenceCode(sourceReferenceCode).catch(() => null);
        }
        setStage('confirmed');
      } catch {
        // Payment not yet confirmed — likely mobile money pending
        setStage('waiting');
      }
    };

    attemptVerify();
  }, [reference]);

  useEffect(() => {
    if (signalRStatus === 'failed') setStage('failed');
  }, [signalRStatus]);

  useEffect(() => {
    if (stage !== 'confirmed' || !reference) return;

    const storageKey = `clawfe:pendingOrder:${companyPath || 'global'}`;

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;

      const pendingOrder = JSON.parse(raw);
      if (pendingOrder?.paymentReference === reference) {
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.warn('Failed to clear confirmed pending order:', error);
    }
  }, [stage, reference, companyPath]);

  const handleCopyReference = async () => {
    if (!sourceReferenceCode) return;

    try {
      await navigator?.clipboard?.writeText(sourceReferenceCode);
      setCopiedReference(true);
      window.setTimeout(() => setCopiedReference(false), 2000);
    } catch (error) {
      console.error('Failed to copy reference:', error);
    }
  };

  if (!reference) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto" />
          <h2 className="font-heading font-bold text-xl text-foreground">Invalid Payment Link</h2>
          <p className="text-muted-foreground font-caption">No payment reference found. Please contact support.</p>
          <Button onClick={() => navigate(`/${companyPath}`)}>Back to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ClientHeaderNavigation />

      <main className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <div className="max-w-md w-full text-center space-y-6">

          {stage === 'verifying' && (
            <>
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-foreground">Verifying Payment</h2>
              <p className="text-muted-foreground font-caption">Please wait while we confirm your payment…</p>
            </>
          )}

          {stage === 'waiting' && (
            <>
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <Icon name="Clock" size={36} className="text-amber-600" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-foreground">Waiting for Approval</h2>
              <p className="text-muted-foreground font-caption">
                Check your phone and approve the Mobile Money prompt to complete your order.
              </p>

              {timeoutReached ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                  <p className="text-sm text-amber-800 font-medium">
                    This is taking longer than expected. If you approved the payment, your order is likely confirmed.
                  </p>
                  <p className="text-xs text-amber-700">
                    Reference: <span className="font-mono font-bold">{reference}</span>
                  </p>
                  <Button variant="outline" fullWidth onClick={() => navigate(`/${companyPath}`)}>
                    Back to Home
                  </Button>
                </div>
              ) : (
                <div className="bg-card border border-border rounded-xl p-4">
                  <p className="text-xs text-muted-foreground">
                    Reference: <span className="font-mono font-semibold text-foreground">{reference}</span>
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    <span className="text-xs text-amber-700 font-medium">Listening for confirmation…</span>
                  </div>
                  <Button
                    size="sm"
                    variant={copiedReference ? 'success' : 'outline'}
                    iconName={copiedReference ? 'Check' : 'Copy'}
                    onClick={handleCopyReference}
                    className="mt-3 w-full"
                  >
                    {copiedReference ? 'Copied!' : 'Copy reference'}
                  </Button>
                </div>
              )}
            </>
          )}

          {stage === 'confirmed' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Icon name="CheckCircle" size={40} className="text-green-600" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-foreground">Order Confirmed!</h2>
              <p className="text-muted-foreground font-caption">
                Your payment was successful and your order is being prepared.
              </p>
              <div className="bg-card border border-border rounded-xl p-4">
                <p className="text-xs text-muted-foreground mb-1">Order Reference</p>
                <p className="font-mono font-bold text-lg text-foreground">{sourceReferenceCode}</p>
                <Button
                  size="sm"
                  variant={copiedReference ? 'success' : 'outline'}
                  iconName={copiedReference ? 'Check' : 'Copy'}
                  onClick={handleCopyReference}
                  className="mt-3 w-full"
                >
                  {copiedReference ? 'Copied!' : 'Copy reference'}
                </Button>
              </div>
              <Button fullWidth onClick={() => navigate(`/${companyPath}`)}>
                Continue Shopping
              </Button>
            </>
          )}

          {stage === 'failed' && (
            <>
              <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <Icon name="XCircle" size={40} className="text-destructive" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-foreground">Payment Failed</h2>
              <p className="text-muted-foreground font-caption">
                We could not confirm your payment. Please try again or contact support.
              </p>
              <Button fullWidth onClick={() => navigate(`/${companyPath}`)}>
                Back to Home
              </Button>
            </>
          )}

        </div>
      </main>
    </div>
  );
};

export default OrderPaymentCallback;
